import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent, AgentTemplate, Workspace } from '../types';
import { listAgents, deleteAgent, listTemplates, listWorkspaces } from '../api/client';

export function MyAgents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [templateFilter, setTemplateFilter] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ags, tpls, wss] = await Promise.all([listAgents(), listTemplates(), listWorkspaces()]);
      setAgents(ags);
      setTemplates(tpls);
      setWorkspaces(wss);
    } finally {
      setLoading(false);
    }
  }, []);

  const templateById = useMemo(
    () => new Map(templates.map((t) => [t.id, t])),
    [templates],
  );

  // An agent belongs to a workspace via its template (workspace holds templateIds)
  const visibleAgents = useMemo(() => {
    let result = agents;
    if (templateFilter) result = result.filter((a) => a.templateId === templateFilter);
    if (workspaceFilter) {
      const ws = workspaces.find((w) => w.id === workspaceFilter);
      result = ws ? result.filter((a) => ws.templateIds.includes(a.templateId)) : [];
    }
    return result;
  }, [agents, templateFilter, workspaceFilter, workspaces]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this agent?')) return;
    await deleteAgent(id);
    await load();
  };

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={styles.heading}>My Agents</h2>
        <button style={styles.refreshBtn} onClick={load}>Refresh</button>
      </div>

      {(workspaces.length > 0 || templates.length > 0) && (
        <div style={styles.filters}>
          {workspaces.length > 0 && (
            <select style={styles.select} value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
              <option value="">All workspaces</option>
              {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          {templates.length > 0 && (
            <select style={styles.select} value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}>
              <option value="">All templates</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {(workspaceFilter || templateFilter) && (
            <button style={styles.clearBtn} onClick={() => { setWorkspaceFilter(''); setTemplateFilter(''); }}>Clear</button>
          )}
        </div>
      )}

      {loading ? (
        <div style={styles.muted}>Loading...</div>
      ) : agents.length === 0 ? (
        <div style={styles.muted}>No agents yet. Go to Templates to create one.</div>
      ) : visibleAgents.length === 0 ? (
        <div style={styles.muted}>No agents match the selected filters.</div>
      ) : (
        <div style={styles.list}>
          {visibleAgents.map((a) => (
            <div key={a.id} style={styles.card} onClick={() => navigate(`/agents/${a.id}`)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <strong>{a.name}</strong>
                  <span
                    style={styles.tplBadge}
                    title="Open template"
                    onClick={(e) => { e.stopPropagation(); navigate(`/templates/${a.templateId}`); }}
                  >{templateById.get(a.templateId)?.name ?? 'Unknown template'}</span>
                </div>
                <span style={styles.date}>{new Date(a.createdAt).toLocaleDateString()}</span>
              </div>

              <div style={styles.keyRow}>
                <code
                  style={styles.apiKey}
                  title="Agent ID — click to copy (API key is on the agent page)"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(a.id);
                    setCopiedId(a.id);
                    setTimeout(() => setCopiedId(null), 1500);
                  }}
                >{copiedId === a.id ? 'Copied!' : a.id}</code>
              </div>

              {Object.keys(a.customFieldValues).length > 0 && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  {Object.entries(a.customFieldValues).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                </div>
              )}

              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button style={styles.connectBtn} onClick={(e) => { e.stopPropagation(); navigate('/chat', { state: { apiKey: a.apiKey } }); }}>Connect</button>
                <button style={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  refreshBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#555' },
  muted: { color: '#888', fontSize: 14 },
  filters: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 },
  select: { padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 12, color: '#555', maxWidth: 220 },
  clearBtn: { padding: '4px 10px', borderRadius: 6, border: 'none', background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#4f46e5' },
  tplBadge: { fontSize: 11, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', cursor: 'pointer' },
  date: { fontSize: 12, color: '#aaa' },
  keyRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 },
  apiKey: { flex: 1, fontSize: 11, fontFamily: 'monospace', color: '#666', background: '#e2e8f0', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', userSelect: 'none' },
  connectBtn: { padding: '6px 14px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  deleteBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
};
